import React from 'react';
import {
	Box,
	SimpleGrid,
	Heading,
	Text,
	VStack,
	Flex,
	HStack,
	Button,
	Icon,
	Spinner,
	Code,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiPlus, FiSmartphone, FiKey, FiSettings } from 'react-icons/fi';
import Card from 'components/card/Card';
import { usePermissions } from 'hooks/usePermissions';
import { useListSettingsChannelsQuery, useGetSetupQuery } from 'api/metaWhatsappApi';
import ConnectionPanel from './ConnectionPanel';
import SetupChecklist from './SetupChecklist';
import MetaAccountCard from './MetaAccountCard';
import ChannelCard from './ChannelCard';
import WebhookCard from './WebhookCard';
import SetupProgress, { setupSteps } from './SetupProgress';
import InitialAssignment from './InitialAssignment';
import InboundTest from './InboundTest';
import ReadyModal, { hasSeenReady, markSeenReady } from './ReadyModal';
import { StatusPill, ago } from './_shared';

/**
 * WhatsApp settings: get a number connected, then name it, switch it, and say
 * who may use it.
 *
 * ★ THE EMPTY STATE IS THE SETUP. A workspace with nothing connected lands on
 * the two ways to connect, not on a sentence saying nothing is connected.
 *
 * ★ AND A BLOCKED SETUP EXPLAINS ITSELF. When the CRM and the connection
 * service are not wired together there is nothing a tenant can click, so the
 * checklist naming each missing variable comes FIRST — above the doors it would
 * otherwise be hidden behind.
 *
 * What the screen shows comes from two places on purpose: the CRM's own channel
 * rows (names, switches, assignments — things the CRM owns) and the connection
 * service's setup read (accounts, the webhook, Embedded Signup availability —
 * things only it can know). Neither is derived from the other.
 */
/** Where the client sets their WhatsApp token — the first step, and its own screen. */
const SETTINGS_PATH = '/admin-setting/configuration';

/**
 * The one card a workspace sees before it has a token.
 *
 * It names the two keys verbatim, because they are typed into a generic
 * service/key form on another screen where nothing would otherwise tell an
 * administrator what to call them.
 */
function CredentialsFirst() {
	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Box p="4">
				<HStack spacing="2" mb="1">
					<Icon as={FiKey} color="text.accent" />
					<Text fontSize="sm" fontWeight="bold" color="text.primary">
						Start here: add your WhatsApp token
					</Text>
				</HStack>
				<Text fontSize="xs" color="text.secondary" lineHeight="1.7">
					Your access token and API URL belong to this workspace and are set once, in Admin Settings →
					Secrets under the service <Code fontSize="xs">whatsapp</Code>. Every number you connect afterwards
					uses them, and this is the same pair WhatsApp already uses here.
				</Text>

				<VStack align="stretch" spacing="1.5" mt="3" mb="3">
					{[
						['api_url', 'https://graph.facebook.com/v21.0'],
						['token', 'Your permanent access token (starts with EAA…)'],
					].map(([key, hint]) => (
						<HStack key={key} spacing="2" align="baseline">
							<Code fontSize="xs" px="1.5">
								{key}
							</Code>
							<Text fontSize="0.7rem" color="text.muted">
								{hint}
							</Text>
						</HStack>
					))}
				</VStack>

				<Button
					as={RouterLink}
					to={SETTINGS_PATH}
					size="sm"
					colorScheme="green"
					leftIcon={<Icon as={FiSettings} />}
				>
					Open Admin Settings
				</Button>
			</Box>
		</Card>
	);
}

export default function MetaWhatsappSettings({ embedded = false } = {}) {
	const { hasPermission } = usePermissions();
	const canSeeSetup = hasPermission('whatsapp', 'integration.view');
	const canConnect = hasPermission('whatsapp', 'integration.connect');
	const canAssign = hasPermission('whatsapp', 'channel.assign');

	const { data, isLoading, isError, refetch: refetchChannels } = useListSettingsChannelsQuery();
	const rows = data?.channels ?? [];

	const [adding, setAdding] = React.useState(false);
	// ★ WHICH ACCOUNT IS BEING RECONNECTED, IF ANY.
	// Held here rather than in the card because the form that does the work
	// lives in a different branch of this tree — and because opening the panel
	// is this component's decision, not the card's.
	const [reconnect, setReconnect] = React.useState(null);
	// Dismissing step 2 for this visit. Not persisted: the strip still shows the
	// step unfinished, so nothing is hidden — only the prompt is.
	const [skippedAssign, setSkippedAssign] = React.useState(false);
	// A reconnect opens the panel even on a workspace whose numbers are all
	// connected — which is exactly when it is needed, and exactly when the panel
	// is otherwise closed.
	const panelOpen = !isLoading && (rows.length === 0 || adding || Boolean(reconnect));

	// ★ POLLED ONLY WHILE SOMETHING IS ACTUALLY EXPECTED TO CHANGE.
	// Two waits, and both end by themselves: Meta verifying the callback URL,
	// and the first customer message arriving. Neither can be observed any other
	// way — one happens inside Meta's dashboard, the other on someone's phone —
	// so the screen watches, and goes quiet the moment it has its answer. A
	// permanent poll on a settings page is a request every ten seconds for the
	// rest of the day.
	const [poll, setPoll] = React.useState(0);
	const {
		data: setup,
		isFetching,
		refetch: refetchSetup,
	} = useGetSetupQuery(undefined, { skip: !canSeeSetup, pollingInterval: poll });

	const onConnected = () => {
		// Whatever it was, it is done: leaving this set would keep re-seeding the
		// form with ids the operator has already submitted.
		setReconnect(null);
		// Keep the panel up: the next step (the webhook) happens inside Meta and
		// the administrator needs the values on this screen.
		setAdding(true);
		refetchSetup();
		refetchChannels();
		// ★ NO SECOND LOOK. The connect response itself carries the numbers and
		// the CRM writes their rows before answering, so one refetch is the whole
		// story. This used to wait 2.5s for a registry push that could never have
		// created them: that push writes to an in-memory cache, and its payload
		// deliberately carries no display number or verified name to build a card
		// from. The delay hid the missing write rather than covering for it.
	};

	const accounts = setup?.accounts ?? [];

	// ★ TWO READS, ONE NUMBER — JOINED HERE RATHER THAN ON THE SERVER.
	// The CRM's own row owns the name, the send switch and who is assigned. The
	// connection layer owns what the traffic did: whether the number has sent,
	// whether a customer has written to it, what is blocking it, what Meta
	// thinks of its quality. Neither service can answer the other's half, and
	// this screen already fetches both — so it joins them on phoneNumberId
	// instead of making the channel list call the connection layer on every read.
	//
	// The CRM used to recompute the outbound half from its own message
	// collection. That was a second answer to a question already answered, and
	// two answers to one question eventually differ.
	//
	// ⚠ A connection layer that has not shipped this yet simply supplies
	// nothing, and every field below degrades to null — the strip shows the step
	// as unfinished rather than breaking.
	const upstream = React.useMemo(() => {
		const map = new Map();
		for (const c of setup?.channels ?? []) map.set(String(c.phoneNumberId), c);
		return map;
	}, [setup]);

	const channels = React.useMemo(
		() =>
			rows.map((row) => {
				const live = upstream.get(String(row.phoneNumberId));
				return {
					...row,
					lastOutboundAt: live?.lastOutboundAt ?? null,
					lastInboundAt: live?.lastInboundAt ?? null,
					// Names, never a collapsed status: clearing a Meta restriction does
					// not clear a token that also needs re-authenticating.
					blockers: live?.blockers ?? [],
					qualityRating: live?.qualityRating ?? null,
					providerStatus: live?.status ?? row.providerStatus ?? null,
				};
			}),
		[rows, upstream],
	);
	const diagnostics = setup?.diagnostics;
	const blocked = Boolean(setup && setup.available === false);
	// Shown whenever anything is unresolved, and kept (collapsed) once it is not,
	// so an operator who just fixed a variable can watch the rest turn green.
	const showChecklist = canSeeSetup && diagnostics?.items?.length > 0 && (blocked || !diagnostics.ok);

	// ★ THE WORKSPACE'S OWN STEP GATES THE REST.
	// Without a token there is nothing to connect a number WITH, and the server
	// refuses anyway — so the form is replaced by the one thing that unblocks it
	// rather than shown as a door that does not open.
	const needsCredentials = diagnostics?.items?.some(
		(i) => i.id === 'tenant.credentials' && i.status === 'error',
	);

	// ★ A "needs configuration" ROW IS NOT A CONNECTED NUMBER.
	// The list carries two kinds of row: numbers this CRM actually has, and
	// numbers the platform routes here that were never written locally. Counting
	// the second kind here would report a workspace as more finished than it is —
	// the exact opposite of what these counters exist for — and the placeholder
	// has no assignments or routability of its own to count.
	const configured = channels.filter((c) => !c.needsConfiguration);
	const connected = configured.length > 0 || accounts.some((a) => a.status === 'ACTIVE');
	const routable = configured.filter((c) => c.isRoutable).length;
	const count = configured.length;

	// ★ THE FORM NEEDS A PHONE NUMBER ID, AND THE ACCOUNT CARD HAS NONE.
	// One submission re-hydrates every number on the account, so this only has to
	// name one of them — the default if there is one. Channels carry `wabaId`
	// only when the connection layer supplied it, so a row without one is treated
	// as belonging here rather than excluded: on the single-account workspaces
	// this screen is built for that is right, and naming the wrong number of the
	// same account would still reconnect all of them.
	const reconnectTarget = React.useCallback(
		(account) => {
			const mine = configured.filter(
				(c) => !c.wabaId || String(c.wabaId) === String(account.wabaId),
			);
			const pick = mine.find((c) => c.isDefault) ?? mine[0] ?? null;
			return { wabaId: String(account.wabaId ?? ''), phoneNumberId: String(pick?.phoneNumberId ?? '') };
		},
		[configured],
	);

	// ★ THE LAST STEP, COUNTED AT THE TOP.
	// A connected number with nobody on it is invisible to every human in the
	// workspace, so "connected · 1 number · 1 of 1 routable" would read as
	// finished while no message could ever be seen. This is the Part 3 twin of
	// the "0 of 2 set" counter on the Secrets screen.
	// Nobody on it at all, or people on it who cannot open the inbox — two ways
	// to reach the same outcome, so they are counted as one problem.
	const unassigned = configured.filter(
		(c) => (c.assignedUsers ?? 0) === 0 || (c.assignedWithoutAccess ?? 0) >= (c.assignedUsers ?? 0),
	).length;

	// ★ ONE SOURCE FOR "WHERE ARE WE", SHARED WITH THE STRIP.
	// The strip and the wizard below it must never disagree about which step is
	// current, so both read the same derivation rather than each re-deciding.
	const steps = React.useMemo(
		() => setupSteps({ channels, webhook: setup?.webhook }),
		[channels, setup],
	);
	const stepDone = React.useMemo(
		() => Object.fromEntries(steps.map((st) => [st.key, st.done])),
		[steps],
	);
	const allDone = steps.every((st) => st.done);

	// The number being set up: the first that is not finished, else the default.
	const focus = React.useMemo(() => {
		const unstaffed = configured.find((c) => (c.assignedUsers ?? 0) === 0);
		const unproven = configured.find((c) => !c.lastInboundAt);
		return unstaffed ?? unproven ?? configured.find((c) => c.isDefault) ?? configured[0] ?? null;
	}, [configured]);

	React.useEffect(() => {
		// Waiting on Meta's handshake, or on the first inbound message. Both are
		// external events; once both have happened there is nothing left to watch.
		const waiting =
			Boolean(setup?.available) && connected && (!stepDone.webhook || !stepDone.received);
		setPoll(waiting ? 10000 : 0);
	}, [setup, connected, stepDone.webhook, stepDone.received]);

	// ★ SHOWN ONCE, AND ONLY WHEN THE CLAIM IS TRUE.
	// Dismissal lives in the browser rather than the database — see ReadyModal.
	const [ready, setReady] = React.useState(false);
	React.useEffect(() => {
		if (allDone && focus && !hasSeenReady(focus.phoneNumberId)) setReady(true);
	}, [allDone, focus]);

	const tone = !connected
		? 'muted'
		: blocked
			? 'danger'
			: routable === count && unassigned === 0
				? 'success'
				: 'warning';

	return (
		<Box
			pt={embedded ? 0 : { base: '130px', md: '80px', xl: '80px' }}
			px={embedded ? 0 : { base: '4', md: '6' }}
			pb="8"
		>
			{/* ★ CENTRED, NOT CAPPED AT 980px.
			    The shell around this is full-bleed, so a 980px column with no
			    margin pinned every card to the left and left half a 1440px screen
			    empty. 1400px is what the Secrets screen uses, and above that the
			    content centres rather than stretching into unreadable line lengths. */}
			<VStack spacing="4" align="stretch" maxW="1400px" mx="auto">
				<Flex justify="space-between" align="start" flexWrap="wrap" gap="3">
					<Box>
						{!embedded && (
							<Heading size="md" color="text.primary" mb="1">
								WhatsApp numbers
							</Heading>
						)}
						<Flex align="center" flexWrap="wrap" gap="2">
							<Icon as={FiSmartphone} color={tone === 'muted' ? 'text.muted' : tone} boxSize="4" />
							<StatusPill tone={tone}>
								{connected ? `Connected · ${count} number${count === 1 ? '' : 's'}` : 'Not connected'}
							</StatusPill>
							{connected && (
								<Text fontSize="xs" color={unassigned > 0 ? 'warning' : 'text.muted'}>
									{routable} of {count} routable
									{unassigned > 0 &&
										` · ${unassigned} nobody can see yet${
											unassigned === count && count === 1 ? ' — assign someone with WhatsApp access' : ''
										}`}
									{setup?.webhook?.lastSignedEventAt
										? ` · last message from Meta ${ago(setup.webhook.lastSignedEventAt)}`
										: ''}
								</Text>
							)}
						</Flex>
						<Text fontSize="sm" color="text.muted" mt="1">
							{connected
								? 'Name each number, choose whether it sends, and pick who can use it.'
								: 'Connect your WhatsApp Business number to receive and send messages from this CRM.'}
						</Text>
					</Box>

					<HStack spacing="2">
						{connected && canConnect && !adding && !reconnect && (
							<Button
								size="sm"
								variant="outline"
								leftIcon={<Icon as={FiPlus} />}
								// Adding is not reconnecting: a prefilled form would put
								// someone adding a SECOND account on the first one's ids.
								onClick={() => {
									setReconnect(null);
									setAdding(true);
								}}
							>
								Add another account
							</Button>
						)}
						{(adding || reconnect) && count > 0 && (
							<Button
								size="sm"
								variant="ghost"
								onClick={() => {
									setReconnect(null);
									setAdding(false);
								}}
							>
								Close setup
							</Button>
						)}
					</HStack>
				</Flex>

				{isLoading && <Spinner size="sm" />}
				{isError && (
					<Text fontSize="sm" color="danger">
						Settings could not be loaded.
					</Text>
				)}

				{/* What is left to do, in one line, before any of the detail below. */}
				{!isLoading && canSeeSetup && <SetupProgress channels={channels} webhook={setup?.webhook} />}

				{/* First, because nothing below it can be used until it is clear. */}
				{showChecklist && <SetupChecklist diagnostics={diagnostics} defaultOpen={blocked} />}

				{/* The account behind the numbers: where a broken connection actually
				    shows. A wide, short strip, so it reads across rather than down. */}
				{accounts.map((a) => (
					<MetaAccountCard
						key={a.wabaId}
						account={a}
						// Only offered to someone who could actually complete it.
						onReconnect={
							canConnect ? () => setReconnect(reconnectTarget(a)) : undefined
						}
					/>
				))}

				{panelOpen && canSeeSetup && canConnect && needsCredentials && <CredentialsFirst />}

				{panelOpen && canSeeSetup && canConnect && !needsCredentials && (
					<ConnectionPanel setup={setup} onConnected={onConnected} reconnect={reconnect} />
				)}

				{panelOpen && !(canSeeSetup && canConnect) && (
					<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
						<Box>
							<Text fontSize="sm" fontWeight="medium" color="text.primary">
								No WhatsApp number is connected yet
							</Text>
							<Text fontSize="xs" color="text.muted" mt="1">
								Connecting one needs the "Connect or Re-authenticate Meta" permission on the WhatsApp
								module. Ask an administrator who holds it to set it up from this page.
							</Text>
						</Box>
					</Card>
				)}

				{/* ★ THE NUMBERS FILL THE WIDTH THEMSELVES.
				    They were one narrow column beside an aside, which left the right
				    half empty once the webhook card moved to the bottom where its step
				    belongs. Two abreast from lg uses the space with the actual work
				    instead of padding it out with reference material. */}
				<SimpleGrid columns={{ base: 1, lg: 2 }} spacing="4" alignItems="start">
					{channels.map((c) => (
						<ChannelCard
							key={c.phoneNumberId}
							channel={c}
							// The number names its own ids, so no lookup through the account:
							// a row without a mirrored wabaId falls back to the single
							// account this screen is built for, as reconnectTarget does.
							onReconnect={
								canConnect
									? () =>
											setReconnect({
												wabaId: String(c.wabaId ?? accounts[0]?.wabaId ?? ''),
												phoneNumberId: String(c.phoneNumberId ?? ''),
											})
									: undefined
							}
						/>
					))}
				</SimpleGrid>

				{/* ★ STEP 2, SHOWN ONLY WHILE IT IS THE CURRENT ONE.
				    A permanent "assign someone" panel on a finished setup is clutter;
				    this is the wizard's step, and the Assignments tab is where the
				    same job lives forever. Dismissible, because the connection is
				    genuinely complete without it. */}
				{connected && canAssign && !stepDone.people && !skippedAssign && focus && (
					<InitialAssignment channel={focus} onSkip={() => setSkippedAssign(true)} />
				)}

				{/* ★ STEP 5 — the only check that proves the whole chain.
				    Shown once sending works and the webhook is verified, because
				    before that there is a more useful thing to be doing, and hidden
				    once a message has arrived. */}
				{connected && stepDone.webhook && !stepDone.received && focus && (
					<InboundTest channel={focus} isPolling={poll > 0} />
				)}

				{/* ★★ THE WEBHOOK IS LAST ON THE PAGE, NOT JUST LAST IN THE STRIP.
				    It sat at the top of the aside — the first thing beside the numbers
				    — which read as "do this next" however the strip was ordered, and
				    that is where people stopped. It belongs where its step is: after
				    the numbers, after assigning, after proving a message goes out.
				    Sending never needed it; it is what makes replies come back. */}
				{connected && canSeeSetup && (
					<WebhookCard webhook={setup?.webhook} onRefresh={refetchSetup} isRefreshing={isFetching} />
				)}

				<ReadyModal
					channel={focus}
					account={accounts[0]}
					assignedCount={focus?.assignedUsers ?? 0}
					isOpen={ready}
					onClose={() => {
						markSeenReady(focus?.phoneNumberId);
						setReady(false);
					}}
				/>
			</VStack>
		</Box>
	);
}
